import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { fmtDateTime, fmtDistance, fmtDuration, fmtElevation, fmtSpeed } from "../lib/format";
import { deriveActivityName } from "../lib/activityName";
import { getSportKey, getSportVisual } from "../lib/sportVisuals";
import { getMetricVisual, cardMetricsForSport } from "../lib/metricVisuals";
import { resolveActivityCardSpeedMs, resolvePedallingCadence } from "../lib/motionMetrics";
import { usesPaceAnalytics } from "../lib/analyticsRate";
import { SportBadge } from "./SportBadge";

function isPresentNumber(value) {
  return typeof value === "number" && Number.isFinite(value);
}

function buildActivityCardStats(activity, unitSystem) {
  const sport = getSportKey(activity);
  const speedMs = resolveActivityCardSpeedMs(activity);
  const cadence = sport === "cycling"
    ? resolvePedallingCadence(activity)
    : (isPresentNumber(activity.avg_cadence) ? activity.avg_cadence : null);
  const paceMode = usesPaceAnalytics(activity);

  const extras = [];
  const push = (key, label, value, unit) => {
    if (value == null || value === "" || value === "—") return;
    const visual = getMetricVisual(key);
    extras.push({
      key,
      label,
      value,
      unit,
      icon: visual.icon,
      accent: visual.accent,
    });
  };

  if (speedMs != null && speedMs > 0.1) {
    push(
      paceMode ? "pace" : "speed",
      paceMode ? "Pace" : "Speed",
      fmtSpeed(speedMs, activity.sport || sport, unitSystem)
    );
  }
  if (activity.has_hr !== false && isPresentNumber(activity.avg_hr) && activity.avg_hr > 0) {
    push("hr", "Avg HR", `${Math.round(activity.avg_hr)}`, "bpm");
  }
  if (
    sport === "cycling"
    && activity.has_power !== false
    && isPresentNumber(activity.avg_power)
    && activity.avg_power > 0
  ) {
    push("power", "Power", `${Math.round(activity.avg_power)}`, "W");
  }
  if (
    sport === "running"
    && activity.has_power === true
    && isPresentNumber(activity.avg_power)
    && activity.avg_power > 0
  ) {
    push("power", "Power", `${Math.round(activity.avg_power)}`, "W");
  }
  if (
    cadence != null
    && cadence > 0
    && (sport !== "cycling" || activity.has_cadence !== false)
  ) {
    push("cadence", "Cadence", `${Math.round(cadence)}`, "rpm");
  }
  if (isPresentNumber(activity.elevation_gain_m) && activity.elevation_gain_m > 0) {
    const elev = fmtElevation(activity.elevation_gain_m);
    push("elevation", "Elev", elev);
  }
  if (isPresentNumber(activity.calories) && activity.calories > 0) {
    push("calories", "Calories", `${Math.round(activity.calories)}`, "kcal");
  }
  if (sport === "swimming" && isPresentNumber(activity.lap_count) && activity.lap_count > 0) {
    push("laps", "Laps", `${Math.round(activity.lap_count)}`);
  }

  const order = cardMetricsForSport(sport);
  extras.sort((a, b) => {
    const ia = order.indexOf(a.key);
    const ib = order.indexOf(b.key);
    return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib);
  });

  const stats = [];
  const distVisual = getMetricVisual("distance");
  const timeVisual = getMetricVisual("duration");
  if (activity.total_distance_m != null) {
    stats.push({
      key: "distance",
      label: "Dist",
      value: fmtDistance(activity.total_distance_m, unitSystem),
      icon: distVisual.icon,
      accent: distVisual.accent,
    });
  }
  if (activity.duration_seconds != null) {
    stats.push({
      key: "duration",
      label: "Time",
      value: fmtDuration(activity.duration_seconds),
      icon: timeVisual.icon,
      accent: timeVisual.accent,
    });
  }

  for (const extra of extras) {
    if (stats.length >= 4) break;
    if (stats.some((item) => item.key === extra.key)) continue;
    stats.push(extra);
  }

  return stats;
}

export const ActivityCard = ({ activity, index = 0, unitSystem = "metric" }) => {
  const sport = getSportVisual(activity);
  const Icon = sport.icon;
  const stats = buildActivityCardStats(activity, unitSystem);
  const columns = stats.length >= 4 ? "sm:grid-cols-4" : stats.length === 3 ? "sm:grid-cols-3" : "sm:grid-cols-2";

  return (
    <Link
      to={`/activity/${activity.id}`}
      className="block group app-card hover:border-brand-primary hover:shadow-app-md transition-colors fade-in"
      style={{ animationDelay: `${index * 40}ms` }}
      data-testid={`activity-card-${activity.id}`}
    >
      <div className="p-4 sm:p-5 flex items-start justify-between gap-3 border-b border-dark-border">
        <div className="flex items-center gap-3 min-w-0">
          <div
            className="icon-frame group-hover:border-brand-primary transition-colors"
            style={{ color: sport.accent, background: sport.soft, borderColor: `${sport.accent}44` }}
          >
            <Icon className="w-5 h-5" strokeWidth={1.5} aria-hidden="true" />
          </div>
          <div className="min-w-0">
            <div className="card-label">
              {fmtDateTime(activity)}
            </div>
            <div className="flex flex-col items-start sm:flex-row sm:items-center gap-1 sm:gap-2 mt-1 min-w-0">
              <div className="font-heading text-xl tracking-wider uppercase text-dark-text-primary truncate">
                {deriveActivityName(activity)}
              </div>
              <SportBadge activity={activity} />
            </div>
          </div>
        </div>
        <ArrowRight className="w-4 h-4 text-dark-text-muted group-hover:text-brand-primary transition-colors shrink-0 mt-1" strokeWidth={1.5} aria-hidden="true" />
      </div>

      <div className={`grid grid-cols-2 ${columns} divide-y sm:divide-y-0 divide-dark-border`}>
        {stats.map((stat, i) => (
          <Stat
            key={stat.key}
            icon={stat.icon}
            label={stat.label}
            value={stat.value}
            unit={stat.unit}
            accent={stat.accent}
            last={i === stats.length - 1}
            odd={i % 2 === 0}
          />
        ))}
      </div>
    </Link>
  );
};

const Stat = ({ icon: Icon, label, value, unit, accent, last, odd }) => (
  <div
    className={`p-3 sm:p-4 min-w-0 ${odd ? "border-r border-dark-border sm:border-r" : ""} ${last ? "sm:border-r-0" : "sm:border-r sm:border-dark-border"}`}
  >
    <div className="flex items-center gap-1.5 card-label">
      {Icon ? <Icon className="w-3 h-3" strokeWidth={1.5} style={accent ? { color: accent } : undefined} aria-hidden="true" /> : null}
      {label}
    </div>
    <div className="font-mono text-lg text-dark-text-primary mt-1 truncate">
      {value}
      {unit && value !== "—" && (
        <span className="text-[10px] text-dark-text-muted ml-1">{unit}</span>
      )}
    </div>
  </div>
);
